import React, { useState, useMemo, useEffect } from 'react';
import { Layout, Select, Menu, Button, theme, Dropdown, Avatar, Tooltip, Badge } from 'antd';
import {
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    HomeOutlined,
    ShoppingOutlined,
    ShoppingCartOutlined,
    SettingOutlined,
    UserOutlined,
    LogoutOutlined,
    ToolOutlined,
    TagsOutlined,
    FolderOutlined,
    DownOutlined,
    BugOutlined,
    BarChartOutlined,
    MessageOutlined,
    MailOutlined,
    ExperimentOutlined,
    ShopOutlined,
    DollarOutlined,
} from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../redux/slices/authSlice';
import { fetchUnprocessedCount } from '../modules/Inbox/actions';
import { TaskDrawer } from '../components/TaskMonitoring';
import { hasPermission } from '../utils/permissions';
import { AmazonIcon, ShopifyIcon } from '../utils/icons';

const { Header, Content, Footer } = Layout;

const MainLayout = ({ children }) => {
    const [collapsed, setCollapsed] = useState(false);
    const [taskDrawerVisible, setTaskDrawerVisible] = useState(false);
    const [openKeys, setOpenKeys] = useState([]);
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user } = useSelector(state => state.auth);
    const { unprocessedCount } = useSelector(state => state.inbox);

    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    // Get app configuration based on current route
    const getAppConfig = () => {
        if (currentApp === 'amazon-orders') {
            return {
                logo: <AmazonIcon style={{ fontSize: '24px' }} />,
                title: 'Amazon',
                color: '#FF9900',
                menuColor: '#FF9900'
            };
        }
        
        if (currentApp === 'shopify-orders') {
            return {
                logo: <ShopifyIcon style={{ fontSize: '24px' }} />,
                title: 'Shopify',
                color: '#96bf48',
                menuColor: '#96bf48'
            };
        }
        
        // Default: Home/Portal
        return {
            logo: <img src="/logo128.png" alt="YUP" style={{ width: '24px', height: '24px' }} />,
            title: 'YUP PORTAL',
            color: '#f97316',
            menuColor: '#f97316'
        };
    };

    // Calculate currentApp directly from URL - each tab is independent
    const currentApp = useMemo(() => {
        const path = location.pathname;
        
        if (path === '/home') return 'home';
        if (path.startsWith('/system')) return 'home';
        if (path.startsWith('/orders') || path.startsWith('/inbox') || path.startsWith('/financial-transactions')) return 'amazon-orders';
        if (path.startsWith('/shopify')) return 'shopify-orders';
        if (path.startsWith('/ads-tool') || path.startsWith('/ads')) return 'ads-tool';
        if (path.startsWith('/inventory') || path.startsWith('/analytics') || path === '/categories' || path.startsWith('/admin')) return 'amazon-orders';
        
        return 'home'; // default
    }, [location.pathname]);

    const appConfig = getAppConfig();

    // Removed auto-open submenu logic to prevent menu from expanding on reload

    // // Fetch unprocessed message count on component mount and periodically
    useEffect(() => {
        if (user && hasPermission(user, 'order.read')) {
            dispatch(fetchUnprocessedCount());
            
            // // Set up interval to fetch count every 30 seconds
            // const interval = setInterval(() => {
            //     dispatch(fetchUnprocessedCount());
            // }, 30000);

            // return () => clearInterval(interval);
        }
    }, []);

    // Custom styles for menu with dynamic color
    const menuStyles = `
        .ant-menu-horizontal > .ant-menu-item-selected {
            background-color: transparent !important;
            border-bottom: 2px solid ${appConfig.menuColor} !important;
            color: ${appConfig.menuColor} !important;
        }
        
        .ant-menu-horizontal > .ant-menu-submenu-selected {
            background-color: transparent !important;
            border-bottom: 2px solid ${appConfig.menuColor} !important;
            color: ${appConfig.menuColor} !important;
        }
        
        .ant-menu-horizontal > .ant-menu-submenu-selected > .ant-menu-submenu-title {
            color: ${appConfig.menuColor} !important;
        }
        
        .ant-menu-horizontal > .ant-menu-item-selected::after,
        .ant-menu-horizontal > .ant-menu-submenu-selected::after {
            display: none !important;
        }
        
        .ant-menu-horizontal > .ant-menu-item,
        .ant-menu-horizontal > .ant-menu-submenu {
            background-color: transparent !important;
            border-bottom: 2px solid transparent !important;
            transition: all 0.2s ease;
        }
        
        .ant-menu-horizontal > .ant-menu-item:hover,
        .ant-menu-horizontal > .ant-menu-submenu:hover {
            background-color: transparent !important;
            border-bottom: 2px solid #9ca3af !important;
            color: #e5e7eb !important;
        }
        
        .ant-menu-horizontal > .ant-menu-submenu:hover > .ant-menu-submenu-title {
            color: #e5e7eb !important;
        }
        
        .ant-menu-horizontal > .ant-menu-item a {
            color: inherit !important;
        }
        
        .ant-menu-submenu-popup {
            background-color: #1f2937 !important;
        }
        
        .ant-menu-submenu-popup .ant-menu-item-selected {
            background-color: ${appConfig.menuColor}1a !important;
            color: ${appConfig.menuColor} !important;
        }
        
        .ant-menu-submenu-popup .ant-menu-item:hover {
            background-color: rgba(156, 163, 175, 0.1) !important;
            color: #e5e7eb !important;
        }
    `;

    // Function to get page title based on current route
    const getPageTitle = () => {
        switch (location.pathname) {
            case '/':
                return 'Dashboard';
            case '/orders':
                return 'Orders Management';
            case '/inbox':
                return 'Customer Inbox';
            case '/inventory/sku-items':
                return 'SKU Items Management';
            case '/inventory/listings':
                return 'Listings Management';
            case '/analytics/niche':
                return 'Niche Analysis & Statistics';
            case '/categories':
                return 'Categories Management';
            case '/shopify/categories':
                return 'Shopify Categories Management';
            case '/settings':
                return 'Settings';
            case '/profile':
                return 'Profile';
            case '/admin/settings':
                return 'Admin Settings';
            case '/system/settings':
                return 'System Settings';
            case '/system/categories':
                return 'System Categories';
            default:
                return 'Order Management System';
        }
    };

    const handleLogout = () => {
        dispatch(logout()).then(() => {
            navigate('/login');
        });
    };

    // Function to determine current app based on route
    const getCurrentApp = () => {
        const path = location.pathname;
        
        if (path === '/home') return 'home';
        if (path.startsWith('/system')) return 'home';
        if (path.startsWith('/orders') || path.startsWith('/inbox') || path.startsWith('/financial-transactions')) return 'amazon-orders';
        if (path.startsWith('/shopify')) return 'shopify-orders';
        if (path.startsWith('/ads-tool') || path.startsWith('/ads')) return 'ads-tool';
        if (path.startsWith('/inventory') || path.startsWith('/analytics') || path === '/categories' || path.startsWith('/admin')) return 'amazon-orders';
        
        return 'home'; // default
    };

    // Function to get navigation items based on current app
    const getAppNavItems = () => {
        const currentApp = getCurrentApp();
        
        switch (currentApp) {
            case 'home':
                // Home app - minimal navigation
                return [];
                
            case 'amazon-orders':
                // Amazon Orders app navigation
                return [
                    {
                        key: '/orders',
                        icon: <ShoppingOutlined />,
                        label: <Link to="/orders">Orders</Link>,
                        permission: 'order.read',
                    },
                    {
                        key: '/inventory/sku-items',
                        icon: <TagsOutlined />,
                        label: <Link to="/inventory/sku-items">SKU Items</Link>,
                        permission: 'sku_item.read',
                    },
                    {
                        key: '/inventory/listings',
                        icon: <ShopOutlined />,
                        label: <Link to="/inventory/listings">Listings</Link>,
                        permission: 'sku_item.read',
                    },
                    {
                        key: '/analytics/niche',
                        icon: <BarChartOutlined />,
                        label: <Link to="/analytics/niche">Analysis</Link>,
                        permission: 'sku_item.read',
                    },
                    {
                        key: '/categories',
                        icon: <FolderOutlined />,
                        label: <Link to="/categories">Categories</Link>,
                        permission: 'category.read',
                    },
                    {
                        key: '/financial-transactions',
                        icon: <DollarOutlined />,
                        label: <Link to="/financial-transactions">Financial Data</Link>,
                        permission: 'financial_adjustment.read',
                    },
                    {
                        key: '/admin/settings',
                        icon: <ToolOutlined />,
                        label: <Link to="/admin/settings">Settings</Link>,
                        permission: 'admin_setting.read',
                    },
                ];
                
            case 'shopify-orders':
                // Shopify Orders app navigation
                // Dynamic label based on current route
                const productsLabel = location.pathname === '/shopify/variants' ? 'Variants' : 'Products';
                
                return [
                    {
                        key: '/shopify/orders',
                        icon: <ShoppingCartOutlined />,
                        label: <Link to="/shopify/orders">Orders</Link>,
                        permission: 'shopify_order.read',
                    },
                    {
                        key: 'products-submenu',
                        icon: <ShopOutlined />,
                        label: productsLabel,
                        permission: 'shopify_product.read',
                        children: [
                            {
                                key: '/shopify/products',
                                label: <Link to="/shopify/products">Products</Link>
                            },
                            {
                                key: '/shopify/variants',
                                label: <Link to="/shopify/variants">Variants</Link>
                            }
                        ]
                    },
                    {
                        key: '/shopify/categories',
                        icon: <FolderOutlined />,
                        label: <Link to="/shopify/categories">Categories</Link>,
                        permission: 'shopify_category.read',
                    },
                    {
                        key: '/shopify/settings',
                        icon: <ToolOutlined />,
                        label: <Link to="/shopify/settings">Settings</Link>,
                        permission: 'shopify_store.manage',
                    },
                ];
                
            case 'ads-tool':
                // Ads Optimization app navigation
                return [
                    {
                        key: '/ads-tool',
                        icon: <ExperimentOutlined />,
                        label: <Link to="/ads-tool">Rule List</Link>,
                        // Note: No permission check for now - all users can access ads tool
                        // permission: 'ads.read',
                    }
                ];
                
            default:
                return [];
        }
    };

    // Function to get app-specific right side buttons
    const getAppButtons = () => {
        const currentApp = getCurrentApp();
        
        switch (currentApp) {
            case 'home':
                // Home app - no specific buttons
                return null;
                
            case 'amazon-orders':
                // Amazon Orders app - show Inbox button
                return (
                    user && hasPermission(user, 'order.read') && (
                        <Tooltip title="Customer Inbox">
                            <Badge count={unprocessedCount} size='small' offset={[-75, 2]}>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<MailOutlined />}
                                    onClick={() => navigate('/inbox')}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        height: '28px',
                                        padding: '0 8px',
                                        borderRadius: '3px',
                                        color: location.pathname === '/inbox' ? '#f97316' : '#e5e7eb',
                                        backgroundColor: location.pathname === '/inbox' ? 'rgba(249, 115, 22, 0.1)' : 'transparent',
                                        border: `1px solid ${location.pathname === '/inbox' ? '#f97316' : '#374151'}`,
                                        fontSize: '12px',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (location.pathname !== '/inbox') {
                                            e.currentTarget.style.backgroundColor = '#374151';
                                            e.currentTarget.style.borderColor = '#4b5563';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (location.pathname !== '/inbox') {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                            e.currentTarget.style.borderColor = '#374151';
                                        }
                                    }}
                                >
                                    <span style={{ marginLeft: 4, fontSize: 11 }}>Inbox</span>
                                </Button>
                            </Badge>
                        </Tooltip>
                    )
                );
                
            case 'shopify-orders':
                // Shopify Orders app - placeholder for future buttons
                return null;
                // TODO: Add Shopify-specific buttons
                
            case 'ads-tool':
                // Ads Optimization app - show quick access to Flow Builder
                return (
                    user && (
                        <Tooltip title="Create New Rule">
                            <Button
                                type="text"
                                size="small"
                                icon={<ExperimentOutlined />}
                                onClick={() => {
                                    const newRuleId = `new_${Date.now()}`;
                                    navigate(`/ads-tool/builder/${newRuleId}`);
                                }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: '28px',
                                    padding: '0 8px',
                                    borderRadius: '3px',
                                    color: '#e5e7eb',
                                    backgroundColor: 'transparent',
                                    border: '1px solid #374151',
                                    fontSize: '12px',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#374151';
                                    e.currentTarget.style.borderColor = '#4b5563';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.borderColor = '#374151';
                                }}
                            >
                                <span style={{ marginLeft: 4, fontSize: 11 }}>New Rule</span>
                            </Button>
                        </Tooltip>
                    )
                );
                
            default:
                return null;
        }
    };

    // Filter navigation items based on user permissions
    const navItems = useMemo(() => {
        if (!user) return [];
        
        const appNavItems = getAppNavItems();
        
        return appNavItems.filter(item => {
            // If no permission required, show the item
            if (!item.permission) return true;
            
            // Check if user has the required permission
            return hasPermission(user, item.permission);
        }).map(({ permission, ...item }) => item); // Remove permission property from final items
    }, [user, location.pathname]);

    // User dropdown menu items
    const userMenuItems = [
        {
            key: 'profile',
            icon: <UserOutlined />,
            label: <Link to="/profile">Profile</Link>,
        },
        {
            type: 'divider',
        },
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: <span onClick={handleLogout}>Logout</span>,
        },
    ];

    // Remove the old static navItems array since we've moved it above

    return (
        <Layout style={{ minHeight: '100vh' }}>
            {/* Inject custom styles */}
            <style dangerouslySetInnerHTML={{ __html: menuStyles }} />
            
            <Header
                style={{
                    position: 'fixed',
                    top: 0,
                    zIndex: 1000,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    background: '#1f2937',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                    padding: '0 16px',
                    borderBottom: '1px solid #374151',
                    height: '40px',
                }}
            >
                {/* Logo */}
                <Link 
                    to="/home" 
                    style={{ textDecoration: 'none' }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginRight: '24px',
                            minWidth: '100px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = '0.8';
                            e.currentTarget.style.transform = 'scale(1.02)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = '1';
                            e.currentTarget.style.transform = 'scale(1)';
                        }}
                    >
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            marginRight: '4px' 
                        }}>
                            {appConfig.logo}
                        </div>
                        <h2
                            style={{
                                color: appConfig.color,
                                margin: 0,
                                fontSize: '14px',
                                fontWeight: 'bold',
                                letterSpacing: '0.3px',
                                lineHeight: '24px'
                            }}
                        >
                            {appConfig.title}
                        </h2>
                    </div>
                </Link>
 
                {/* Navigation Menu */}
                <Menu
                    mode="horizontal"
                    selectedKeys={[location.pathname]}
                    openKeys={openKeys}
                    onOpenChange={setOpenKeys}
                    items={navItems}
                    theme="dark"
                    style={{
                        flex: 1,
                        minWidth: 0,
                        border: 'none',
                        backgroundColor: 'transparent',
                        fontSize: '12px',
                        lineHeight: '40px',
                    }}
                />

                {/* Task Monitor Button and User Dropdown */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {/* App-specific buttons */}
                    {getAppButtons()}

                    {/* Task Monitor Button */}
                    <Tooltip title="Task Monitor">
                        <Button
                            type="text"
                            size="small"
                            icon={<BugOutlined />}
                            onClick={() => setTaskDrawerVisible(true)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                height: '28px',
                                padding: '0 8px',
                                borderRadius: '3px',
                                color: '#e5e7eb',
                                backgroundColor: 'transparent',
                                border: '1px solid #374151',
                                fontSize: '12px',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#374151';
                                e.currentTarget.style.borderColor = '#4b5563';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.borderColor = '#374151';
                            }}
                        >
                            <span style={{ marginLeft: 4, fontSize: 11 }}>Tasks</span>
                        </Button>
                    </Tooltip>

                    {/* User Dropdown */}
                    <Dropdown
                        menu={{ items: userMenuItems }}
                        placement="bottomRight"
                        trigger={['click']}
                    >
                        <Button
                            type="text"
                            size="small"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                height: '28px',
                                padding: '0 6px',
                                borderRadius: '3px',
                                color: '#e5e7eb',
                                backgroundColor: 'transparent',
                                border: '1px solid #374151',
                                fontSize: '12px',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#374151';
                                e.currentTarget.style.borderColor = '#4b5563';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.borderColor = '#374151';
                            }}
                        >
                            <Avatar
                                size={20}
                                icon={<UserOutlined />}
                                style={{
                                    marginRight: '4px',
                                    backgroundColor: '#4b5563'
                                }}
                            />
                            <span style={{
                                marginRight: '3px',
                                fontSize: '12px',
                                color: '#e5e7eb'
                            }}>{user?.username || user?.email || 'User'}</span>
                            <DownOutlined style={{
                                fontSize: '8px',
                                color: '#9ca3af'
                            }} />
                        </Button>
                    </Dropdown>
                </div>
            </Header>

            <Layout style={{ marginTop: '40px' }}>
                <Content
                    style={{
                        minHeight: 'calc(100vh - 80px)',
                        backgroundColor: '#f0f0f0',
                    }}
                >
                    <div
                    >
                        {children}
                    </div>
                </Content>
                
                {/* <Footer
                    style={{
                        textAlign: 'center',
                        backgroundColor: '#f0f0f0',
                        color: 'rgba(0, 0, 0, 0.5)',
                        padding: '12px 0',
                        fontSize: '12px'
                    }}
                >
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <span>© {new Date().getFullYear()}</span>
                        <span style={{ 
                            color: '#f97316',
                            fontWeight: 'bold',
                            background: 'linear-gradient(135deg, #f97316 0%, #ea580c 50%, #f97316 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text'
                        }}>
                            Yupmedia
                        </span>
                        <span>- All rights reserved</span>
                    </div>
                </Footer> */}
            </Layout>

            {/* Task Monitoring Drawer */}
            <TaskDrawer 
                visible={taskDrawerVisible}
                onClose={() => setTaskDrawerVisible(false)}
            />
        </Layout>
    );
};

export default MainLayout;
